import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import isString from 'lodash/isString';
import URL from 'url-parse';

export function buildAssetUrlWithSizes(assetUrl, size) {
  if (typeof assetUrl === 'string' && typeof size === 'string') {
    const matchData = size.match(/w_(\d*)/);
    const width = get(matchData, '1');

    return width
      ? `${assetUrl.replace('/upload/', `/upload/${size}/`)} ${width}w`
      : '';
  }

  return '';
}

export function buildAssetUrlWithAuto(src) {
  return src && typeof src === 'string'
    ? src.replace('upload/', 'upload/w_auto,c_fill/')
    : '';
}

export function buildSrcSet(assetSizes, propsToSrcSetForSize, src) {
  const srcSetItems = get(assetSizes, propsToSrcSetForSize) || [];
  return srcSetItems.map(item => buildAssetUrlWithSizes(src, item)).join(', ');
}

export function makeHttps(url) {
  if (!isString(url)) {
    return null;
  }
  const parsed = new URL(url);
  if (parsed.protocol !== 'https:') {
    parsed.set('protocol', 'https:');
    return parsed.toString();
  }

  return url;
}

export function buildAssetUrlWithPresets(src, size, isDesktop) {
  const smallSizes = get(size, 'small.srcset');
  const smallSizesLength = get(smallSizes, 'length');
  const largestSmallPreset = get(smallSizes, `[${smallSizesLength - 1}]`);
  const presetSize = isDesktop
    ? get(size, 'large.srcset[0]')
    : largestSmallPreset;

  if (!isString(src)) {
    return null;
  }

  const parsed = new URL(src);

  if (
    parsed.host === 'fast-company-res.cloudinary.com' ||
    parsed.host === 'assets.fastcompany.com'
  ) {
    parsed.set(
      'pathname',
      parsed.pathname.replace('/upload/', `/upload/${presetSize}/`)
    );
    return parsed.toString();
  }

  return src;
}

export function changeAssetDomain(url) {
  if (!isString(url)) {
    return null;
  }

  const parsed = new URL(url);

  if (parsed.host === 'fast-company-res.cloudinary.com') {
    parsed.set('host', 'assets.fastcompany.com');
    return parsed.toString();
  }

  if (parsed.host === 'wp-cms-fastcompany-com.s3.amazonaws.com') {
    parsed.set('host', 'assets.fastcompany.com');
    const pathname = parsed.pathname;
    parsed.set('pathname', `/image/upload/wp-cms${pathname}`);
    return parsed.toString();
  }

  return url;
}

export function convertGIFToVideoAssets(assetUrl) {
  if (!isString(assetUrl) || isUndefined(assetUrl)) {
    return null;
  }

  const parsed = new URL(assetUrl);
  const formats = ['jpg', 'webm', 'mp4'];
  const assets = {};

  if (
    parsed.host === 'fast-company-res.cloudinary.com' ||
    parsed.host === 'assets.fastcompany.com'
  ) {
    formats.forEach(function createAssetUrlForEachFormat(format) {
      assets[format] = assetUrl.replace('/upload/', `/upload/f_${format}/`);
      if (format === 'mp4' || format === 'webm') {
        assets[format] = assets[format]
          .replace(',q_auto:best', ',q_70')
          .replace(',q_auto', ',q_70');
      }
    });
    return assets;
  }

  return assetUrl;
}

export const imageSizesLookup = {
  column: {
    large: {
      sizes: '10vw',
      srcset: [
        'w_141,ar_16:9,c_fill,f_auto,q_auto,fl_lossy',
        'w_280,ar_16:9,c_fill,f_auto,q_auto,fl_lossy'
      ]
    },
    small: {
      sizes: '25vw',
      srcset: ['w_141,f_auto,q_auto,fl_lossy', 'w_236,f_auto,q_auto,fl_lossy']
    }
  },
  4: {
    large: {
      sizes: '25vw',
      srcset: ['w_280,f_auto,q_auto,fl_lossy']
    },
    small: {
      sizes: '25vw',
      srcset: ['w_141,f_auto,q_auto,fl_lossy', 'w_236,f_auto,q_auto,fl_lossy']
    }
  },
  3: {
    large: {
      sizes: '33vw',
      srcset: ['w_386,ar_16:9,c_fill,f_auto,q_auto,fl_lossy']
    },
    small: {
      sizes: '25vw',
      srcset: [
        'w_141,ar_16:9,c_fill,f_auto,q_auto,fl_lossy',
        'w_236,ar_16:9,c_fill,f_auto,q_auto,fl_lossy'
      ]
    }
  },
  2: {
    large: {
      sizes: '50vw',
      srcset: ['w_600,f_auto,q_auto,fl_lossy']
    },
    small: {
      sizes: '25vw',
      srcset: ['w_141,f_auto,q_auto,fl_lossy', 'w_236,f_auto,q_auto,fl_lossy']
    }
  },
  1: {
    large: {
      sizes: '66vw',
      srcset: ['w_813,f_auto,q_auto,fl_lossy']
    },
    small: {
      sizes: '90vw',
      srcset: ['w_300,f_auto,q_auto,fl_lossy', 'w_400,f_auto,q_auto,fl_lossy']
    }
  },
  poster: {
    large: {
      sizes: '50vw',
      srcset: ['w_707,f_auto,q_auto:best,fl_lossy']
    },
    small: {
      sizes: '90vw',
      srcset: [
        'w_300,f_auto,q_auto:best,fl_lossy',
        'w_400,f_auto,q_auto:best,fl_lossy'
      ]
    }
  },
  meta: {
    large: {
      sizes: '100vw',
      srcset: ['w_1280,f_auto,q_auto,fl_lossy']
    }
  },
  slider: {
    large: {
      sizes: '66vw',
      srcset: ['w_707,h_397,c_fit,f_auto,q_auto:best,fl_lossy']
    },
    small: {
      sizes: '90vw',
      srcset: [
        'w_300,h_168,c_fit,f_auto,q_auto:best,fl_lossy',
        'w_394,h_221,c_fit,f_auto,q_auto:best,fl_lossy'
      ]
    }
  },
  bigFirstMobile: {
    large: {
      sizes: '33vw',
      srcset: ['w_386,ar_16:9,c_fill,f_auto,q_auto,fl_lossy']
    },
    small: {
      sizes: '90vw',
      srcset: [
        'w_300,ar_16:9,c_fill,f_auto,q_auto,fl_lossy',
        'w_400,ar_16:9,c_fill,f_auto,q_auto,fl_lossy'
      ]
    }
  }
};



// WEBPACK FOOTER //
// ./src/utils/cloudinary_asset_util.js